/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  SessionAPIResponse,
  SessionInitialProperties,
  PersonalSessionInterface,
} from '../../interface/charging-session.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import paymentApi from '../../utils/api/payments';

import { setNotificationMessage } from './NotificationManagementSlice';

// Define the type of data expected
interface FetchParameters {
  search?: string;
  page?: number;
  limit?: number;
  deviceType?: string;
}

// a thunk to fetch sessions (payments v2)
export const fetchAllPersonalSessions = createAsyncThunk<
  SessionAPIResponse<PersonalSessionInterface>,
  FetchParameters
>(
  'personal-charging-session/fetchAllPersonalSessions',
  async (data: FetchParameters, { dispatch }) => {
    const response = await paymentApi
      .fetchPersonalChargingSessions(removeEmptyKeys(data))
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return {
      ...response.data,
      results: response.data.results.map((roleData: any) => ({
        ...roleData,
        date: ConvertUTCtoDate(roleData.createdAt),
        id: roleData._id,
      })),
    };
  },
);

// a thunk to fetch session filters
export const fetchListPersonalSessionFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('personal-charging-session/fetchListPersonalSessionFilters', async (_, { dispatch }) => {
  try {
    const response = await paymentApi.fetchChargingSessionsListFilters({ type: 'personal' });
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch(
      setNotificationMessage({
        type: 'error',
        message: error?.response?.data?.message,
        code: error?.response?.data?.code,
        status: true,
      }),
    );
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// Apply default filters
const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);

/**
 * Filter date format func
 * @param {any} date date
 * @returns {string} date string
 */
const formatDate = (date: Date): string => date.toISOString().split('T')[0];

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
  {
    name: 'createdDateRange',
    key: 'startDate_endDate',
    value: '',
    key1: 'startDate',
    value1: formatDate(yesterday),
    key2: 'endDate',
    value2: formatDate(today),
    type: 'dateRange',
    listOfValue: [],
    applyFilter: true,
    enable: true,
    disableClearFilter: true,
    disableClearFilterMessage: 'Date filter is required!',
    FEAddedFilter: true, // This is used to identify if the filter is added from FE
  },
];

// Initial state
const initialState: SessionInitialProperties<PersonalSessionInterface> = {
  payments: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
  TotalFinising: 0,
  totalInprocess: 0,
  totalterminated: 0,
};

// Create the slice
export const personalChargingSessionSlice = createSlice({
  name: 'personal-charging-session',
  initialState,
  reducers: {
    /**
     * Update session filters
     * @param {any} state state
     * @param {any} action action
     */
    updatePersonalSessionFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state state
     */
    disablePersonalSessionFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state state
     * @param {any} action action
     */
    setPersonalSessionSortData: (
      state,
      action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>,
    ) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchAllPersonalSessions.pending,
        (state: SessionInitialProperties<PersonalSessionInterface>) => {
          state.loading = true;
        },
      )
      .addCase(
        fetchAllPersonalSessions.fulfilled,
        (
          state: SessionInitialProperties<PersonalSessionInterface>,
          action: { payload: SessionAPIResponse<PersonalSessionInterface> },
        ) => {
          state.loading = false;
          state.payments = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
          state.TotalFinising = action.payload.TotalFinising;
          state.totalInprocess = action.payload.totalInprocess;
          state.totalterminated = action.payload.totalterminated;
        },
      )
      .addCase(
        fetchAllPersonalSessions.rejected,
        (state: SessionInitialProperties<PersonalSessionInterface>) => {
          state.loading = false;
          // Handle error if needed
        },
      );
    builder
      .addCase(fetchListPersonalSessionFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListPersonalSessionFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload].filter(
            (object, index, self) => index === self.findIndex((t) => t.key === object.key),
          );
        },
      )
      .addCase(fetchListPersonalSessionFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  updatePersonalSessionFilters,
  disablePersonalSessionFiltersApplied,
  setPersonalSessionSortData,
} = personalChargingSessionSlice.actions;
export default personalChargingSessionSlice.reducer;
